import { Divider } from '@sunwebgroup/uds-ui/divider'
import { ListItem } from '@sunwebgroup/uds-ui/list-item'
import { Tag } from '@sunwebgroup/uds-ui/tag'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { useIntl } from '@/intl'
import { getCancellationPolicyUrl } from '@/scopes/account/booking-details/domain/policies/cancellationPolicyUrl'
import { useBookingDetailsStore } from '@/scopes/account/booking-details/infrastructure/useBookingDetailsStore'
import {
  getFullCancellationPaymentDueDate,
} from '@/scopes/account/booking-details/pages/selectors/fullCancellationSelectors'
import { renderExternalLink } from '@/scopes/account/booking-details/pages/utils/renderExternalLink'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import styles from './FullCancellationWithCostConfirmationStep.module.css'

export function FullCancellationWithCostConfirmationStep() {
  const { formatMessage, formatCurrency } = useIntl()
  const locale = useConfiguration('locale')
  const siteName = useConfiguration('siteName')
  const cancellationPolicyUrl = getCancellationPolicyUrl(siteName)
  const supportUrl = useConfiguration('links.support')
  const costs = useBookingDetailsStore(state => state.fullCancellationCosts)

  if (costs.status !== 'success' || !costs.isCancellationWithCostsAllowed)
    return null

  const amountToPay = costs.amountToPay ?? 0
  const isRefund = amountToPay < 0
  const paymentDueDate = getFullCancellationPaymentDueDate(locale)

  return (
    <div className={styles.container}>
      <Typography variant="body-m-regular-snug" as="p">
        {formatMessage(
          { id: 'booking_details_cancel_booking_full_cancellation_with_cost_sheet_text' },
          { link: renderExternalLink(cancellationPolicyUrl ?? '') },
        )}
      </Typography>
      <Typography variant="body-m-regular-snug" as="p">
        {formatMessage({ id: 'booking_details_cancel_booking_full_cancellation_with_cost_sheet_overview_title' })}
      </Typography>
      <ListItem variant="outlined">
        <div className={styles.costCard}>
          <div className={styles.row}>
            <div className={styles.labelWithSubtext}>
              <Typography variant="body-m-regular-tight" as="span">
                {formatMessage({ id: 'booking_details_cancel_booking_full_cancellation_with_cost_sheet_cancellation_cost_label' })}
              </Typography>
            </div>
            <div className={styles.rowRightAlign}>
              <Typography variant="body-m-bold-flat" as="span">
                {formatCurrency(costs.cancellationCost ?? 0)}
              </Typography>
              <Typography className={styles.supportingText} variant="body-s-regular-snug" as="span">
                {formatMessage({ id: 'booking_details_cancel_booking_full_cancellation_with_cost_sheet_cancellation_cost_percentage_text' })}
              </Typography>
            </div>
          </div>
          <div className={styles.row}>
            <Typography variant="body-m-regular-tight" as="span">
              {formatMessage({ id: 'booking_details_cancel_booking_full_cancellation_with_cost_sheet_amount_paid_label' })}
            </Typography>
            <div className={styles.rowRightAlign}>
              <Tag variant="success-tinted">
                {formatCurrency(costs.amountPaid ?? 0)}
              </Tag>
            </div>
          </div>
          <Divider />
          <div className={styles.amountToPayGroup}>
            <div className={styles.emphasizedRow}>
              <Typography variant="body-l-bold-tight" as="span">
                {formatMessage({
                  id: isRefund
                    ? 'booking_details_cancel_booking_full_cancellation_with_cost_sheet_amount_to_receive_label'
                    : 'booking_details_cancel_booking_full_cancellation_with_cost_sheet_amount_to_pay_label',
                })}
              </Typography>
              <Typography variant="body-l-bold-tight" as="span">
                {formatCurrency(Math.abs(amountToPay))}
              </Typography>
            </div>
            {!isRefund && (
              <Typography className={styles.supportingText} variant="body-s-regular-snug" as="p">
                {formatMessage(
                  { id: 'booking_details_cancel_booking_full_cancellation_with_cost_sheet_payment_due_date_text' },
                  { paymentDueDate },
                )}
              </Typography>
            )}
          </div>
        </div>
      </ListItem>
      <Typography variant="body-m-regular-snug" as="p" className={styles.transferText}>
        {formatMessage(
          { id: 'booking_details_cancel_booking_full_cancellation_with_cost_sheet_transfer_text' },
          { link: renderExternalLink(supportUrl) },
        )}
      </Typography>
    </div>
  )
}
